import type { Placement } from '@floating-ui/react';
import { useField } from 'formik';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { TradingMarketFilter } from 'src/apis/network-points/types';
import { useFormMode } from 'src/components/form/form-mode-context';
import { NetworkPointPopover } from 'src/components/form/select/network-point-select/network-point-popover';
import { useNetworkPointSelectSearch } from 'src/components/form/select/network-point-select/use-network-point-select-search';
import {
  SearchableSingleSelect,
  searchableSingleSelectBaseSchema,
  SearchableSingleSelectContent,
} from 'src/components/form/select/searchable-single-select';
import { requiredOutput } from 'src/components/form/zod-utilities';
import { Spinner } from 'src/components/spinner-container';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

const baseNetworkPointSchema = z.object({
  id: z.string(),
  name: z.string(),
  assignedId: z.string(),
  identifier: z.string(),
  eic: z.string().optional(),
  favourite: z.boolean(),
  score: z.number().optional(),
  active: z.boolean(),
});

const directionMetaSchema = z.object({
  operatorShortName: z.string(),
  operatorId: z.string(),
  marketArea: z.string(),
});

export const networkPointSchema = z.discriminatedUnion('bundle', [
  baseNetworkPointSchema.extend({
    bundle: z.literal(false),
    operatorShortName: z.string(),
    operatorId: z.string(),
    marketArea: z.string(),
    direction: z.enum(['EXIT', 'ENTRY']),
  }),
  baseNetworkPointSchema.extend({
    bundle: z.literal(true),
    exit: directionMetaSchema,
    entry: directionMetaSchema,
  }),
]);

export const nullableNetworkPointSelectSchema = z
  .object({
    networkPoint: networkPointSchema.nullable().transform(requiredOutput()),
  })
  .merge(searchableSingleSelectBaseSchema)
  .nullable();

export const networkPointSelectSchema =
  nullableNetworkPointSelectSchema.transform(requiredOutput());

export type NetworkPointOption = z.input<
  typeof nullableNetworkPointSelectSchema
>;
export type ValidNetworkPointOption = z.output<typeof networkPointSelectSchema>;

type NetworkPointSelectProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  stacked?: boolean;
  inline?: boolean;
  initialSearch?: string;
  disabled?: boolean;
  disabledMessage?: ReactNode;
  placeholder?: string;
  initialOpen?: boolean;
  hint?: string;
  onChange?: (value: string | null) => void;
  leftAddon?: ReactNode;
  placement?: Placement;
  mode?: 'sidebar' | 'default';
  markAsRequired?: boolean;
  tradingMarket?: TradingMarketFilter;
};

export function NetworkPointSelect(props: NetworkPointSelectProps) {
  const [field, fieldMeta, fieldHelper] = useField<NetworkPointOption>(
    props.name
  );
  const handleInitialSearch =
    // if someone set an initial field value, but no networkPoint was set, we trigger an initial search
    (field.value && !field.value.networkPoint ? field.value.label : null) ??
    undefined;

  const { isStacked } = useDefaultStacked();

  const {
    name,
    label,
    placeholder,
    stacked = isStacked,
    inline,
    hideLabel = false,
    disabled,
    disabledMessage,
    initialOpen,
    hint,
    initialSearch = handleInitialSearch,
    onChange,
    leftAddon,
    placement,
    mode = 'default',
    markAsRequired,
    tradingMarket,
  } = props;

  const formMode = useFormMode();

  const {
    networkPointsRoot,
    internalParams,
    setInternalParams,
    params,
    networkPoints,
    loadMoreNetworkPoints,
    searchValue,
    setSearchValue,
    onFocus,
    searchValueForCurrentOptions,
  } = useNetworkPointSelectSearch({
    initialSearch,
    tradingMarket,
  });

  useEffect(() => {
    // this is only relevant if we had a forced initial search,
    // in that case we'll try to find the network point for the initial value
    // (and if we can't find the network point, we'll set the field value to undefined)
    if (!networkPoints.response) return;
    if (!handleInitialSearch) return;
    const networkPoint = networkPoints.response.data._embedded.items.find(
      (item) => item.id === field.value?.value
    );
    if (networkPoint)
      fieldHelper.setValue({
        label: networkPoint.name,
        value: networkPoint.id,
        networkPoint,
      });
    else fieldHelper.setValue(null);
    // oxlint-disable-next-line exhaustive-deps
  }, [networkPoints.response]);

  const pending = networkPoints.pending;
  const options = networkPoints.response?.data._embedded.items.map(
    (item) =>
      ({
        label: item.name,
        value: item.id,
        networkPoint: item,
      }) satisfies ValidNetworkPointOption
  );

  return (
    <SearchableSingleSelect
      name={name}
      label={label}
      placeholder={placeholder}
      stacked={stacked}
      inline={inline}
      disabled={disabled}
      disabledMessage={disabledMessage}
      setSearchValue={setSearchValue}
      options={options}
      searchValue={searchValue}
      searchValueForCurrentOptions={searchValueForCurrentOptions}
      pending={networkPoints.pending}
      hint={hint}
      hideLabel={hideLabel}
      leftAddon={leftAddon}
      markAsRequired={markAsRequired}
      popoverProps={(props) => ({
        ...props,
        className: cn(props.className, 'max-h-full w-[60rem]'),
        'data-testid': networkPoints.response
          ? 'nwp-search-results-ready'
          : undefined,
      })}
      renderOptions={() => (
        <>
          {/* if we had a forced initial search, but no response yet,
            we show a pending state as we miss the network point data */}
          {!handleInitialSearch ? (
            <NetworkPointPopover
              internalParams={internalParams}
              setInternalParams={setInternalParams}
              params={params}
              networkPointsRoot={networkPointsRoot}
              networkPoints={networkPoints}
              loadMoreNetworkPoints={loadMoreNetworkPoints}
              formMode={formMode}
              pending={pending}
              items={networkPoints.response?.data._embedded.items}
              name={name}
              searchValueForCurrentOptions={searchValueForCurrentOptions}
              type="single"
              selectedId={field.value?.networkPoint?.id}
            />
          ) : (
            <SearchableSingleSelectContent>
              Initial search in progress... <Spinner />
            </SearchableSingleSelectContent>
          )}
        </>
      )}
      onChange={onChange}
      initialOpen={initialOpen}
      placement={placement}
      onFocus={onFocus}
      className={cn(
        mode === 'sidebar' &&
          `
            rounded-xs! border-border-sidebar-default!
            bg-background-sidebar-default! text-foreground-sidebar-default
            not-italic!
            placeholder:text-foreground-sidebar-subdued
          `
      )}
      hideChevron={mode === 'sidebar'}
    />
  );
}
